import { useCallback, useRef, useState } from "react";
import { Box, GlobalStyles, Paper, Typography } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, GridApi } from "ag-grid-community";
import { ErrorModal } from "@dexteel/mesf-core";

import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { usePartialRollsets } from "../hooks/usePartialRollsets";
import type { PartialRollsetRow } from "../models/PartialRollsets";
import { PartialRollsetsSummary } from "./PartialRollsetsSummary";
import { PartialRollsetsToolbar } from "./PartialRollsetsToolbar";

const CENTER_CELL = { textAlign: "center" } as const;
const LEFT_CELL = { textAlign: "left" } as const;
const CENTER_HEADER = "ag-cell-center-header";

const toFixedOrEmpty = (value: unknown, decimals: number): string =>
	value == null ? "" : Number(value).toFixed(decimals);

const columnDefs: ColDef<PartialRollsetRow>[] = [
	{
		field: "StandNumber",
		headerName: "Stand",
		width: 100,
		sort: "asc",
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "MillSize",
		headerName: "Mill Size",
		width: 120,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "BarrelDescription",
		headerName: "Description",
		flex: 1,
		minWidth: 200,
		sortable: true,
		cellStyle: LEFT_CELL,
		tooltipField: "BarrelDescription",
	},
	{
		field: "RollsetName",
		headerName: "Roll Set",
		width: 140,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "RollPosition",
		headerName: "Roll Position",
		width: 120,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "Diameter",
		headerName: "Roll Diameter",
		width: 130,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => toFixedOrEmpty(p.value, 2),
	},
	{
		field: "CurrentPercent",
		headerName: "Current Percent",
		width: 140,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => toFixedOrEmpty(p.value, 2),
	},
	{
		field: "CurrentValue",
		headerName: "Current Value",
		width: 130,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => toFixedOrEmpty(p.value, 2),
	},
	{
		field: "RollName",
		headerName: "Foundry Number",
		width: 150,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
];

export const PartialRollsetsReport = () => {
	const [errorMsg, setErrorMsg] = useState("");
	const gridApiRef = useRef<GridApi<PartialRollsetRow> | null>(null);

	const dataQuery = usePartialRollsets();

	const rows = dataQuery.data ?? [];

	const handleRefresh = useCallback(() => {
		dataQuery.refetch();
	}, [dataQuery]);

	const handleExportToExcel = useCallback(() => {
		if (!gridApiRef.current) return;
		gridApiRef.current.exportDataAsExcel({
			fileName: "Partial_Rollsets.xlsx",
			processCellCallback: (params) => {
				const colId = params.column.getColId();
				if (
					colId === "Diameter" ||
					colId === "CurrentPercent" ||
					colId === "CurrentValue"
				) {
					return toFixedOrEmpty(params.value, 2);
				}
				return params.value ?? "";
			},
		});
	}, []);

	if (dataQuery.error && !errorMsg) {
		setErrorMsg((dataQuery.error as Error).message);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 130px)",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Partial Rollsets
			</Typography>
			<GlobalStyles
				styles={{
					".ag-cell-center-header .ag-header-cell-label": {
						justifyContent: "center",
					},
				}}
			/>
			<Paper elevation={3}>
				<PartialRollsetsToolbar
					onRefresh={handleRefresh}
					onExportExcel={handleExportToExcel}
					isLoading={dataQuery.isFetching}
				/>
			</Paper>

			<PartialRollsetsSummary total={rows.length} />

			<Paper
				elevation={3}
				sx={{
					flex: 1,
					overflow: "hidden",
				}}
			>
				{dataQuery.isLoading ? (
					<CenteredLazyLoading />
				) : (
					<AgGridReact<PartialRollsetRow>
						columnDefs={columnDefs}
						rowData={rows}
						rowModelType="clientSide"
						defaultColDef={{ resizable: true }}
						suppressContextMenu
						headerHeight={32}
						rowHeight={28}
						onGridReady={(e) => {
							gridApiRef.current = e.api;
						}}
					/>
				)}
			</Paper>

			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
